import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { useLazyQuery, useMutation } from '@apollo/client';
import { MutationHookOptions } from '@apollo/client/react/types/types';

import { invoiceApi } from '../api';
import { collectInvoiceFromVO } from '../lib';
import { useNotifications } from '../../notification';
import { UpdateInvoiceResponse, UpdateInvoiceVariables } from '../api/types';

export function useInvoice() {
  const { showNotification } = useNotifications();

  const [fetchInvoice] = useLazyQuery(invoiceApi.getByID);

  const [updateInvoice, { loading: isInvoiceUpdating }] = useMutation<
    UpdateInvoiceResponse,
    Partial<UpdateInvoiceVariables>
  >(invoiceApi.update);

  const handleFetchInvoice = async (id: string) => {
    try {
      const { data } = await fetchInvoice({ variables: { id } });

      if (!data?.invoice) return null;

      return collectInvoiceFromVO(data.invoice)!;
    } catch (error) {
      logError(error);
      return null;
    }
  };

  const handleUpdateInvoice = useCallback(
    async (variables: Partial<UpdateInvoiceVariables>) => {
      try {
        await updateInvoice({ variables });
      } catch (error) {
        logError(error);
      }
    },
    [updateInvoice]
  );

  const [applyFeesAndTaxes, { loading: isApplyingFeesAndTaxes }] = useMutation(invoiceApi.applyCharges);

  const handleApplyFeesAndTaxes = useCallback(
    async (
      voId: string,

      fetchOptions?: (api: typeof invoiceApi) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        const { data } = await applyFeesAndTaxes({
          variables: { input: { voId } },
          ...(typeof fetchOptions === 'function' ? fetchOptions(invoiceApi) : {}),
        });

        if (data?.result?.error || !data?.result?.success) {
          showNotification(data?.result?.error ?? 'Something went wrong', { type: 'error' });
          return;
        }

        showNotification('Fees and Taxes applied successfully!');
      } catch (error) {
        showNotification('Something went wrong', { type: 'error' });
        logError(error);
      }
    },
    [applyFeesAndTaxes, showNotification]
  );

  return {
    invoiceApi,
    isInvoiceUpdating,
    handleFetchInvoice,
    handleUpdateInvoice,
    isApplyingFeesAndTaxes,
    handleApplyFeesAndTaxes,
  };
}
